// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The entropy heuristic.
 *
 * The obvious design — "Shannon entropy >= 3.5 bits/char over 20+ characters" — is broken in
 * BOTH directions, and measurement is the only way to see it:
 *
 *   thequickbrownfoxjumpsoverthelazydogagain   H = 4.534   -> flagged (it is English)
 *   a 40-char git SHA-1                         H = 3.909   -> flagged
 *   a UUIDv4                                    H = 3.885   -> flagged
 *   uniform random 20-char hex (mean of 2000)   H = 3.363   -> MISSED (it is a real secret)
 *
 * The cause is that maximum achievable entropy at length L is log2(L) — 4.32 at 20 characters —
 * so a constant bits/char threshold is length-coupled by construction.
 *
 * What ships instead, in order, with all five conditions required:
 *   1. unbounded candidate extraction (a capped tokeniser splits a 1400-char blob into pieces
 *      and the tail then fires)
 *   2. split on internal '=' so KEY=value is two tokens, preserving base64 padding
 *   3. structural suppressors, BEFORE entropy
 *   4. at least two character classes — this single rule removes SHAs, UUIDs, digests and
 *      English word-runs in one move
 *   5. LENGTH-NORMALISED entropy eta = H / log2(min(len, observed alphabet)), threshold 0.92
 *
 * This rule still ships DISABLED by default. It is the noisiest thing in the tool by
 * construction, and a scanner that cries wolf gets turned off.
 */

export interface EntropyVerdict {
  readonly pass: boolean;
  readonly eta: number;
  readonly classes: number;
  readonly suppressor: string | null;
}

export const DEFAULT_ETA_THRESHOLD = 0.92;

/** Unigram Shannon entropy in bits per character. */
export function shannon(token: string): number {
  if (token.length === 0) return 0;
  const freq = new Map<string, number>();
  for (const ch of token) freq.set(ch, (freq.get(ch) ?? 0) + 1);
  let h = 0;
  const n = token.length;
  for (const c of freq.values()) {
    const p = c / n;
    h -= p * Math.log2(p);
  }
  return h;
}

/** How many of {upper, lower, digit, symbol} appear. */
export function charClasses(token: string): number {
  let c = 0;
  if (/[A-Z]/.test(token)) c++;
  if (/[a-z]/.test(token)) c++;
  if (/[0-9]/.test(token)) c++;
  if (/[^A-Za-z0-9]/.test(token)) c++;
  return c;
}

/** Distinct characters actually observed — the alphabet the token could plausibly draw from. */
function observedAlphabet(token: string): number {
  const s = new Set<string>();
  for (const ch of token) s.add(ch);
  return s.size;
}

/**
 * Extract entropy candidates from a decoded string.
 *
 * The upper bound is UNBOUNDED on purpose. A `{24,512}` cap makes a 1,400-character base64 image
 * split into [512, 512, 376] and the 376-char tail sails past the bulk suppressor.
 */
export function extractCandidates(text: string): string[] {
  const out: string[] = [];
  const re = /[A-Za-z0-9+/=_-]{24,}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    for (const piece of splitOnEquals(m[0])) {
      if (piece.length >= 24) out.push(piece);
    }
  }
  return out;
}

/**
 * Split on internal `=` while preserving trailing base64 padding.
 * `DEFAULT_MAX_RETRY_ATTEMPTS_SECONDS=30` becomes one token, not one giant one.
 */
export function splitOnEquals(token: string): string[] {
  const trailing = /=+$/.exec(token);
  const pad = trailing ? trailing[0] : '';
  const body = pad ? token.slice(0, -pad.length) : token;
  const parts = body.split('=');
  if (parts.length === 0) return [];
  if (pad) parts[parts.length - 1] = (parts[parts.length - 1] ?? '') + pad;
  return parts.filter((p) => p.length > 0);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** Case-INSENSITIVE, and 48 is included: an uppercase 40-hex digest otherwise fires at eta 0.97. */
const HEX_DIGEST_RE =
  /^(?:[0-9a-f]{32}|[0-9a-f]{40}|[0-9a-f]{48}|[0-9a-f]{56}|[0-9a-f]{64}|[0-9a-f]{96}|[0-9a-f]{128})$/i;
const ISO8601_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:?\d{2})?$/;
const SEMVER_RE = /^v?\d+(?:\.\d+){1,3}(?:[-+][A-Za-z0-9.-]+)?$/;
/** Scrubjay's own output, so scrub(scrub(x)) === scrub(x). */
const PSEUDONYM_RE = /^[A-Z][A-Z0-9]{1,23}_[0-9A-HJKMNP-TV-Z]{10,13}$/;
const REDACTION_RE = /^\[?REDACTED:[A-Z0-9_]{1,48}\]?$/;

/** Common English fragments — enough to veto prose runs without shipping a dictionary. */
const COMMON_FRAGMENTS = [
  'the', 'and', 'ing', 'ion', 'ent', 'for', 'you', 'that', 'with', 'this', 'have', 'not',
  'are', 'was', 'his', 'her', 'they', 'from', 'com', 'org', 'net', 'http', 'www', 'over',
  'quick', 'brown', 'jump', 'lazy', 'dog', 'state', 'art', 'implement', 'default', 'max',
  'retry', 'attempt', 'second', 'minute', 'error', 'request', 'response', 'server', 'client',
  'user', 'name', 'value', 'true', 'false', 'null', 'undefined', 'function', 'return',
  'module', 'export', 'import', 'class', 'const', 'string', 'number', 'object', 'array',
];

/**
 * Does this look like language rather than randomness?
 *
 * Measured cost, published in the limits panel: this suppressor causes 95.7% of all missed
 * secrets at length 20 — about 4.4% of real 20-character random secrets. We keep it because
 * this rule is INFO and off by default, and the alternative is flagging every English sentence.
 */
export function looksWordy(token: string): boolean {
  const lower = token.toLowerCase();
  let hits = 0;
  for (const f of COMMON_FRAGMENTS) {
    if (lower.includes(f)) {
      hits++;
      if (hits >= 3) return true;
    }
  }
  // A long run with no digits and no case variety is prose or an identifier, not a secret.
  if (!/[0-9]/.test(token) && !/[A-Z]/.test(token) && token.length >= 24 && hits >= 1) return true;
  return false;
}

/**
 * Base64 magic prefixes for embedded media and archives. A 60-character base64 PNG header has
 * three character classes and a high eta, so nothing but a magic-number test silences it.
 */
const MEDIA_B64_PREFIXES = [
  'iVBORw0KGgo', // PNG
  '/9j/', // JPEG
  'R0lGOD', // GIF
  'UklGR', // RIFF / WEBP
  'JVBERi0', // PDF
  'H4sI', // gzip
  'UEsDB', // zip
  'PD94bWw', // <?xml
  'AAABAA', // ICO
  'SUkq', // TIFF (LE)
  'T2dnUw', // Ogg
  'AAAAI', // MP4 ftyp
];

/** Does this token look like a filesystem path rather than a random string? */
function looksLikePath(token: string): boolean {
  if (!token.includes('/')) return false;
  const segs = token.split('/');
  if (segs.length < 3) return false;
  let wordy = 0;
  for (const s of segs) {
    if (s.length >= 2 && /^[a-z][a-z0-9._-]*$/.test(s)) wordy++;
  }
  return wordy >= 3;
}

/** Returns the name of the suppressor that silenced this token, or null. */
export function structuralSuppressor(token: string): string | null {
  if (PSEUDONYM_RE.test(token)) return 'pseudonym';
  if (REDACTION_RE.test(token)) return 'redaction-marker';
  if (HEX_DIGEST_RE.test(token)) return 'hex-digest';
  if (UUID_RE.test(token.replace(/^urn:uuid:/i, ''))) return 'uuid';
  if (ISO8601_RE.test(token)) return 'iso8601';
  if (SEMVER_RE.test(token)) return 'semver';
  if (token.length >= 512) return 'b64-bulk';
  for (const p of MEDIA_B64_PREFIXES) if (token.startsWith(p)) return 'media-b64';
  if (looksLikePath(token)) return 'path';
  if (/^[0-9a-f]+$/i.test(token)) return 'single-class';
  if (/^[a-z]+$/.test(token) || /^[A-Z]+$/.test(token) || /^[0-9]+$/.test(token)) {
    return 'single-class';
  }
  if (looksWordy(token)) return 'wordy';
  return null;
}

export function entropyVerdict(token: string, etaThreshold = DEFAULT_ETA_THRESHOLD): EntropyVerdict {
  const classes = charClasses(token);
  const suppressor = structuralSuppressor(token);
  if (suppressor !== null) return { pass: false, eta: 0, classes, suppressor };
  if (classes < 2) return { pass: false, eta: 0, classes, suppressor: 'single-class' };
  const alphabet = Math.min(token.length, observedAlphabet(token));
  const denom = Math.log2(Math.max(alphabet, 2));
  const eta = denom === 0 ? 0 : shannon(token) / denom;
  return { pass: eta >= etaThreshold, eta, classes, suppressor: null };
}
